// Thin HTTP client for the FAFI API (§12.6). Same-origin `/api/v1`; a Bearer authToken (the account credential,
// §0.6) is attached when present. Responses are the `{error:{code,message}}` envelope — a non-2xx throws an
// ApiError carrying the status + code so the UI can react (401 ⇒ sign out, 429 ⇒ back off, 422 ⇒ show message).
// `fetch` is injected so the whole surface is unit-testable without a network.

export interface ApiError {
  status: number;
  code: string;
  message: string;
}

export function isApiError(e: unknown): e is ApiError {
  return typeof e === 'object' && e !== null && 'status' in e && 'code' in e;
}

type FetchLike = (url: string, init: { method: string; headers: Record<string, string>; body?: string }) => Promise<{
  status: number;
  ok: boolean;
  text: () => Promise<string>;
}>;

export class ApiClient {
  constructor(
    private readonly getToken: () => string | null = () => null,
    private readonly baseUrl = '/api/v1',
    private readonly fetchFn: FetchLike = (url, init) => fetch(url, init) as unknown as ReturnType<FetchLike>,
  ) {}

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    const token = this.getToken();
    if (token) headers['authorization'] = `Bearer ${token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    const res = await this.fetchFn(this.baseUrl + path, { method, headers, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
    const raw = await res.text();
    let parsed: unknown = undefined;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = undefined;
      }
    }
    if (!res.ok) {
      const env = (parsed as { error?: { code?: string; message?: string } } | undefined)?.error;
      throw { status: res.status, code: env?.code ?? `HTTP_${res.status}`, message: env?.message ?? 'request failed' } satisfies ApiError;
    }
    return parsed as T; // 204 / empty body ⇒ undefined
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }
  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }
  delete<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('DELETE', path, body);
  }
}
